import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { IdentityAuthPath } from '@/features/auth/lib/constants'
import { env } from '@/config/env'
import { getSubdomainFromHostname } from '@/lib/subdomain'
import { getSurveyTenantContext } from '@/lib/survey-tenant-context'
import { logger } from '@/lib/logger'
import { ApiError, type ApiResponse } from './types'
import { envelopeDataRecord, handleAxiosResponseError } from './interceptorHelpers'
import { API_REQUEST_TIMEOUT_MS } from './requestTimeout'

/** Lets the browser apply `Set-Cookie` from the refresh response before the next request. */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    queueMicrotask(() => {
      resolve()
    })
  })
}

let csrfToken: string | null = null

/** Single-flight refresh so parallel 401s share one POST /refresh. */
let refreshFlight: Promise<void> | null = null

export function setCsrfToken(token: string | null) {
  csrfToken = token
}

function authUrl(suffix: string): string {
  const base = env.VITE_API_BASE_URL.trim()
  return `${base}${suffix}`
}

async function refreshSessionCookies(): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken
  }
  if (globalThis.window !== undefined) {
    const subdomain = getSubdomainFromHostname(
      globalThis.window.location.hostname,
    )
    if (subdomain) {
      headers['x-subdomain'] = subdomain
    }
  }

  await axios.post(
    authUrl(IdentityAuthPath.REFRESH),
    {},
    { withCredentials: true, headers, timeout: API_REQUEST_TIMEOUT_MS },
  )
}

type MePayload = { csrf_token?: string }

async function syncCsrfFromMe(): Promise<void> {
  const headers: Record<string, string> = {}
  if (globalThis.window !== undefined) {
    const subdomain = getSubdomainFromHostname(
      globalThis.window.location.hostname,
    )
    if (subdomain) {
      headers['x-subdomain'] = subdomain
    }
  }

  const { data: envelope } = await axios.get<ApiResponse<MePayload>>(
    authUrl(IdentityAuthPath.ME),
    { withCredentials: true, headers, timeout: API_REQUEST_TIMEOUT_MS },
  )

  const token = envelope?.data?.csrf_token
  if (typeof token === 'string' && token.length > 0) {
    setCsrfToken(token)
  }
}

async function ensureSessionRefreshed(): Promise<void> {
  refreshFlight ??= (async () => {
    await refreshSessionCookies()
    await yieldToBrowser()
    await syncCsrfFromMe()
  })().finally(() => {
    refreshFlight = null
  })
  await refreshFlight
}

function shouldAttemptRefresh(
  status: number,
  config: InternalAxiosRequestConfig | undefined,
): config is InternalAxiosRequestConfig {
  if (status !== 401 || !config) return false
  if (config.__refreshRetried) return false
  const url = config.url ?? ''
  if (url.includes('/api/v1/auth/refresh')) return false
  return true
}

type AxiosErrorLike = {
  response?: { status?: number; data?: unknown }
  config?: InternalAxiosRequestConfig
}

async function handleResponseErrorWithRefresh(
  client: AxiosInstance,
  error: unknown,
): Promise<unknown> {
  if (error instanceof ApiError) {
    return handleAxiosResponseError(error)
  }

  const err = error as AxiosErrorLike
  const status = err.response?.status ?? 0
  const failingConfig = err.config

  if (
    shouldAttemptRefresh(status, failingConfig) &&
    globalThis.window !== undefined
  ) {
    failingConfig.__refreshRetried = true
    try {
      await ensureSessionRefreshed()
      return client.request(failingConfig)
    } catch (error_) {
      logger.warn('session_refresh_failed', {
        url: failingConfig.url ?? 'unknown',
        err: error_,
      })
    }
  }

  return handleAxiosResponseError(error)
}

export function attachInterceptors(client: AxiosInstance) {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (config.data instanceof FormData) {
      // Let the browser set multipart boundary — default application/json serializes File as {}.
      config.headers.setContentType(false)
    }

    if (
      csrfToken &&
      config.method &&
      !['get', 'head', 'options'].includes(config.method)
    ) {
      config.headers.set('X-CSRF-Token', csrfToken)
    }

    if (globalThis.window !== undefined) {
      const subdomain = getSubdomainFromHostname(
        globalThis.window.location.hostname,
      )
      if (subdomain) {
        config.headers.set('x-subdomain', subdomain)
      }
      const surveyTenant = getSurveyTenantContext()
      if (surveyTenant.tenantId) {
        config.headers.set('x-tenant-id', surveyTenant.tenantId)
      }
      if (surveyTenant.tenantSubdomain && !subdomain) {
        config.headers.set('x-subdomain', surveyTenant.tenantSubdomain)
      }
    }

    return config
  })

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse
      if (body?.success === false && body.error) {
        throw new ApiError(
          body.message,
          response.status,
          body.error.code,
          body.error.details,
          envelopeDataRecord(body.data),
        )
      }
      return response
    },
    (error) => handleResponseErrorWithRefresh(client, error),
  )
}
